import React, {
    SetStateAction,
    useState,
    useEffect,
    ReactNode,
    isValidElement
} from "react";

import {
    ExportableAdgColorTypes,
    IconNameTypes,
    ProductKeyTypes,
    Row
} from "../../../../libraries/wpl/foundations/src/index";
import { Card, CardProps } from "../../card/src/index";
import { HeaderTagTypes } from "../../content-block/src";
import { ContentBlock, OneLinkProps } from "../../content-block/src/index";
import { Image, ImageProps } from "../../image/src/index";
import { Link } from "../../link/src/index";
import { Video, VideoProps } from "../../video/src/index";

import {
    TabGroupStyle,
    tabStyle,
    selectContainerStyle,
    quoteSidebar,
    quote,
    contentBlockStyle,
    tabPanel
} from "./tab-group.styles";

export interface TabColorProps {
    defaultBG?: ExportableAdgColorTypes;
    defaultText?: ExportableAdgColorTypes;
    hoverBG?: ExportableAdgColorTypes;
    hoverText?: ExportableAdgColorTypes;
    activeBG?: ExportableAdgColorTypes;
    activeText?: ExportableAdgColorTypes;
    quoteColor?: ExportableAdgColorTypes;
}

export interface ImageDisplayProps {
    imageProps: ImageProps;
}

export interface VideoDisplayProps {
    videoProps: VideoProps;
}

export type DisplayProps = ImageDisplayProps | VideoDisplayProps | JSX.Element;

export interface IndividualTabProps {
    textLabel: string;
    isForceDisplayTextLabelOnPill?: boolean;
    id: string;
}

export interface IndividualTabPropsCustom extends IndividualTabProps {
    component: JSX.Element;
    iconBefore?: JSX.Element;
}

export interface IndividualTabPropsCards extends IndividualTabProps {
    cards: CardProps[];
}

export interface IndividualTabPropsQuote extends IndividualTabProps {
    display: DisplayProps;
    bodyText?: ReactNode;
    headerTextType?: HeaderTagTypes;
    headerText?: ReactNode;
    iconName?: IconNameTypes;
    iconColor?: ExportableAdgColorTypes;
    quoteImageProps?: ImageProps;
    quote?: string;
    name?: string;
    company?: ReactNode;
    ctaProps?: OneLinkProps;
}

export interface IndividualTabPropsImageFullWidth extends IndividualTabProps {
    display: DisplayProps;
    headerText?: ReactNode;
    bodyText?: ReactNode;
}

export type TabsTypes =
    | IndividualTabPropsCustom
    | IndividualTabPropsCards
    | IndividualTabPropsQuote
    | IndividualTabPropsImageFullWidth;

export type TabContentTypes =
    | "custom"
    | "cards"
    | "quoteTextImage"
    | "textImageFullWidth";

export interface TabGroupProps {
    tabContentType: TabContentTypes;
    tabs: TabsTypes[];
    id: string;
    label: string;
    dataProductKey: ProductKeyTypes;
    colors?: TabColorProps;
    onMobileChangeHandler?: React.Dispatch<SetStateAction<object>>;
    onDesktopClickHandler?: React.Dispatch<SetStateAction<object>>;
}

export interface CardNums {
    6: number;
    5: number;
    4: number;
    3: number;
    2: number;
}

const cardRules: CardNums = {
    6: 1,
    5: 2,
    4: 2,
    3: 3,
    2: 4
};

const CardStyleDisplay = (tab: IndividualTabPropsCards) => {
    return (
        <Row>
            {tab.cards.map((card: CardProps, index: number) => {
                return (
                    <Card
                        key={index}
                        {...card}
                        aspectRatio={tab.cards.length < 3 ? "16/9" : "3/2"}
                    />
                );
            })}
        </Row>
    );
};

export const renderTabContent = (props: {
    tab: IndividualTabPropsQuote | IndividualTabPropsImageFullWidth;
}) => {
    const tabDisplay = props.tab.display;
    let elementToDisplay = null;

    if ("videoProps" in tabDisplay && tabDisplay.videoProps.videoLink?.length) {
        elementToDisplay = <Video {...tabDisplay.videoProps} />;
    } else if (
        "imageProps" in tabDisplay &&
        tabDisplay.imageProps.src?.length &&
        tabDisplay.imageProps.alt?.length
    ) {
        elementToDisplay = <Image {...tabDisplay.imageProps} />;
    } else if (isValidElement(tabDisplay)) {
        elementToDisplay = tabDisplay;
    }

    return elementToDisplay;
};

export const Quote = (props: {
    image?: JSX.Element;
    quote?: string;
    name?: string;
    company?: ReactNode;
    colors?: TabColorProps;
}) => {
    return (
        <div className="quote-wrapper" css={quote(props.colors)}>
            {props.image ? props.image : null}
            <p className="quote-blurb">{props.quote}</p>
            {props.name ? <p className="quote-name">{props.name}</p> : null}
            {props.company ? (
                <p className="quote-company">{props.company}</p>
            ) : null}
        </div>
    );
};

const QuoteTextImage = (
    tab: IndividualTabPropsQuote,
    colors?: TabColorProps
) => {
    return (
        <Row layout="4-8">
            <div css={quoteSidebar}>
                {tab.headerText ? (
                    <ContentBlock
                        css={contentBlockStyle}
                        headerText={tab.headerText}
                        bodyText={tab.bodyText}
                        headerType={tab.headerTextType ?? "h3"}
                        iconName={tab.iconName}
                    />
                ) : null}
                {tab.quote ? (
                    <Quote
                        image={
                            tab.quoteImageProps && (
                                <Image {...tab.quoteImageProps} />
                            )
                        }
                        quote={tab.quote}
                        name={tab.name}
                        company={tab.company}
                        colors={colors}
                    />
                ) : null}

                {tab.ctaProps && tab.ctaProps.primaryLinkProps ? (
                    <Link {...tab.ctaProps.primaryLinkProps} />
                ) : null}
            </div>
            {renderTabContent({ tab })}
        </Row>
    );
};

const TextImageFullWidth = (tab: IndividualTabPropsImageFullWidth) => {
    return (
        <Row>
            <div>
                {tab.headerText ? (
                    <div className="content-block" css={contentBlockStyle}>
                        <ContentBlock
                            headerText={tab.headerText}
                            bodyText={tab.bodyText}
                            headerType="h2"
                            isCentered={true}
                        />
                    </div>
                ) : null}
                {renderTabContent({ tab })}
            </div>
        </Row>
    );
};

const TabContent = (
    tabContentType: TabContentTypes,
    tab: TabsTypes,
    tabsLength: number,
    colors?: TabColorProps
) => {
    const cardRule = cardRules[tabsLength as keyof CardNums];

    switch (tabContentType) {
        case "custom":
            if (!("component" in tab)) {
                throw new Error("Must provide a `customComponent` JSX node");
            }
            return tab.component;
        case "cards":
            if (!("cards" in tab)) {
                throw new Error(
                    "Must provide an array of card values in the `cards` tab prop"
                );
            } else if (tab.cards.length > cardRule) {
                throw new Error(
                    `For a group of ${tabsLength} tabs, no more than ${cardRule} cards per tab`
                );
            }
            return CardStyleDisplay(tab);
        case "quoteTextImage":
            if (!("display" in tab)) {
                throw new Error("Please provide image or video props");
            }
            return QuoteTextImage(tab, colors);
        case "textImageFullWidth":
            if (!("display" in tab)) {
                throw new Error("Please provide image or video props");
            }
            return TextImageFullWidth(tab);
        default:
            return null;
    }
};

const updateURL = (tabId?: string) => {
    const newParams = new URLSearchParams(window.location.search);
    if (tabId) {
        newParams.set("tab", tabId);
        console.log(newParams.toString());
        window.history.pushState({}, "", `?${newParams.toString()}`);
    }
};

export const TabGroup = (props: TabGroupProps) => {
    const [currentTab, setCurrentTab] = useState(0);
    const {
        tabContentType,
        tabs,
        colors,
        id,
        label,
        dataProductKey,
        onDesktopClickHandler,
        onMobileChangeHandler
    } = props;

    if (!id || !label) {
        throw new Error(
            '"id" and "label" props are required for accessibility!'
        );
    }

    if (tabs.length > 6 || tabs.length < 2) {
        throw new Error(
            "Must have a minimum of 2 and a maximum of 6 tabs per group!"
        );
    }

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const paramID = params.get("tab");
        const matchingTab = tabs.findIndex((tab) => {
            return tab.id === paramID;
        });

        if (matchingTab >= 0) {
            setCurrentTab(matchingTab);
        }
    }, [currentTab]);

    return (
        <div className="tab-group">
            {/* desktop pill display */}

            <div css={TabGroupStyle} role="tablist">
                <Row>
                    <div className="tab-group-inner">
                        {tabs.map((tab: TabsTypes, index: number) => {
                            return (
                                <button
                                    type="button"
                                    role="tab"
                                    key={`tab-${index}-${tab.id}`}
                                    value={tab.textLabel}
                                    css={tabStyle(index, currentTab, colors)}
                                    aria-selected={index === currentTab}
                                    aria-controls={tab.id}
                                    id={tab.id}
                                    data-event-component="linkButton"
                                    data-event-container="tabContent"
                                    data-event="clicked"
                                    data-label={tab.textLabel}
                                    data-product-key={dataProductKey}
                                    onClick={(
                                        event: React.MouseEvent<HTMLButtonElement>
                                    ) => {
                                        setCurrentTab(index);
                                        updateURL(tab.id);
                                        if (onDesktopClickHandler) {
                                            onDesktopClickHandler(event);
                                        }
                                    }}
                                >
                                    {(tab as IndividualTabPropsCustom)
                                        .iconBefore ??
                                        (tab as IndividualTabPropsCustom)
                                            .iconBefore}
                                    {tab.isForceDisplayTextLabelOnPill
                                        ? tab.textLabel
                                        : tab.headerText || tab.textLabel}
                                </button>
                            );
                        })}
                    </div>
                </Row>
            </div>

            {/* mobile select */}
            <div
                className="select-container"
                css={selectContainerStyle(colors)}
            >
                <Row>
                    <div className="select-container-inner">
                        <label htmlFor={id} aria-label={label}>
                            <select
                                id={id}
                                className="select"
                                css={tabStyle(currentTab, currentTab, colors)}
                                value={
                                    tabs[currentTab]?.textLabel ||
                                    tabs[0]?.textLabel
                                }
                                onChange={(
                                    event: React.ChangeEvent<HTMLSelectElement>
                                ) => {
                                    const selectedIndex =
                                        event?.target?.selectedOptions[0]
                                            ?.dataset["index"];
                                    const selectedId =
                                        event?.target?.selectedOptions[0]
                                            ?.dataset["id"];
                                    setCurrentTab(Number(selectedIndex));
                                    updateURL(selectedId);
                                    if (onMobileChangeHandler) {
                                        onMobileChangeHandler(event);
                                    }
                                }}
                            >
                                {tabs.map((tab: TabsTypes, index: number) => {
                                    return (
                                        <option
                                            data-index={index}
                                            data-id={tab.id}
                                            data-event-component="linkButton"
                                            data-event-container="tabContent"
                                            data-event="clicked"
                                            data-label={tab.textLabel}
                                            data-product-key={dataProductKey}
                                            value={tab.textLabel}
                                            key={`option-${index}-${tab.id}`}
                                            css={tabStyle(
                                                index,
                                                currentTab,
                                                colors
                                            )}
                                            aria-selected={index === currentTab}
                                            aria-describedby={`tab-${tab.id}`}
                                        >
                                            {tab.isForceDisplayTextLabelOnPill
                                                ? tab.textLabel
                                                : tab.headerText ||
                                                  tab.textLabel}
                                        </option>
                                    );
                                })}
                            </select>
                        </label>

                        <svg
                            width="15"
                            height="8"
                            viewBox="0 0 15 8"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M1.5 1L7.5 7L13.5 1"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </div>
                </Row>
            </div>

            {/* tab panel content */}
            <div className="tab-panels">
                {tabs.map((tab: TabsTypes, index: number) => {
                    return (
                        <div
                            className="tab-panel"
                            css={tabPanel}
                            key={`tabpanel-${index}-${tab.id}`}
                            aria-hidden={index !== currentTab}
                            id={`tab-${tab.id}`}
                        >
                            {TabContent(
                                tabContentType,
                                tab,
                                tabs.length,
                                colors
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
